import {Component, computed, inject, Input, ChangeDetectorRef} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {AdxAgentBookingService} from '../../service/adx-agent-booking.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AzureAuthService} from 'src/infrastructure/services/azure-auth.service';

@Component({
    selector: 'app-adx-agent-booking-info',
    templateUrl: './adx-agent-booking-info.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class AdxAgentBookingInfoComponent {
  @Input() public data: any;
  _adxAgentBooking : any;
  isEdit = false;
  adxAgentBookingForm: FormGroup = this.fb.group({});
  currentAgentId: number = 0;
  autocompleteMode: 'A' | 'I' | 'V' = 'A';
  dataSourceId: number = 0;
  formChanges$?: Subscription;
  private readonly azureAuthService = inject(AzureAuthService);
  protected readonly loggedInUserHasDirectorOrFinanceOrManagerRole = computed(() => (this.azureAuthService.checkUserAzureTokenHasManagerRole() && this.azureAuthService.checkUserAzureTokenHasFinanceRole() && this.azureAuthService.checkUserAzureTokenHasDirectorRole()));

  constructor(
    private fb: FormBuilder,
    private adxAgentBookingService: AdxAgentBookingService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef) {
  }

  ngOnInit(){
    this.getAdxAgentBookingInfo();
    this.createForm();
  }

  getAdxAgentBookingInfo() {
    if (this.data != null){
      this._adxAgentBooking = this.data;
      this.dataSourceId = this._adxAgentBooking.agent.dataSourceId;
    }
  }

  toggleEdit(): void {
    if (this.isEdit){
      this._adxAgentBooking.agent.adxAgentId = this.currentAgentId;
      this.resetForm();
    }
    else{
      this.currentAgentId = this._adxAgentBooking.agent.adxAgentId;
      this._adxAgentBooking.agent.adxAgentId = null;
      this.clearForm();
    }

    this.isEdit = !this.isEdit;
    this.cdr.detectChanges();
  }

  createForm(): void {
    this.adxAgentBookingForm = this.fb.group({
      agent: [],
      agentName: [
        this._adxAgentBooking.agent?.agentName,
        Validators.required,
      ],
      firstName: [
        this._adxAgentBooking.agent?.firstName
      ],
      middleName: [
        this._adxAgentBooking.agent?.middleName
      ],
      lastName: [
        this._adxAgentBooking.agent?.lastName
      ],
      interfaceId: [
        this._adxAgentBooking.agent?.interfaceId
      ],
      email: [
        this._adxAgentBooking.agent?.email
      ],
      rate: [
        this._adxAgentBooking.rate
      ],
      amount: [
        this._adxAgentBooking.amount
      ],
    });

    this.formChanges$ = this.adxAgentBookingForm.controls['agent'].valueChanges.subscribe((value) => {
      if (this._adxAgentBooking.adxBooking.adxAgentBookings
          .filter((agentBooking: any) => agentBooking !== null && agentBooking.id !== this._adxAgentBooking.id)
          .some((agent: any) => agent.agentId === value.id && !agent.isDeleted)) {
        this._snackBar.open('Agent already assigned to this service.', 'Dismiss', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['message-position', 'app-notification-error']
        });

        this.toggleEdit();

        return;
      }

      const agentDisplayName = value.agentName || 
                               [value.firstName, value.middleName, value.lastName]
                                 .filter((n: string) => n)
                                 .join(' ');
     
      this.adxAgentBookingForm?.patchValue({
        agentName: agentDisplayName,
        firstName: value.firstName,
        middleName: value.middleName,
        lastName: value.lastName,
        interfaceId: value.interfaceId,
        email: value.email,
      });
    });
  }

  clearForm(): void {
    this.adxAgentBookingForm?.patchValue({
      agentName: null,
      firstName: null,
      middleName: null,
      lastName: null,
      interfaceId: null,
      email: null,
      rate: null,
      amount: null,
    });
  }

  resetForm(): void {
    this.adxAgentBookingForm?.patchValue({
      agentName: this._adxAgentBooking.agent?.agentName,
      firstName: this._adxAgentBooking.agent?.firstName,
      middleName: this._adxAgentBooking.agent?.middleName,
      lastName: this._adxAgentBooking.agent?.lastName,
      interfaceId: this._adxAgentBooking.agent?.interfaceId,
      email: this._adxAgentBooking.agent?.email,
      rate: this._adxAgentBooking.rate,
      amount: this._adxAgentBooking.amount,
    });
  }

  updateAgent(){
    const selectedAgent = this.adxAgentBookingForm.value.agent;
    const formValues = this.adxAgentBookingForm.getRawValue();
    
    this._adxAgentBooking.agentId = selectedAgent.id;
    this._adxAgentBooking.agentBookingCentralDocumentId = selectedAgent.tramsCentralDocumentId;
    
    this.adxAgentBookingService.updateAdxAgentBooking(this._adxAgentBooking)
      .subscribe({
          next: () => {
            this._snackBar.open('Successfully updated agent booking information!!', 'Dismiss', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['message-position', 'app-notification-success']
            });
            
            const updatedAgent = {
              ...this._adxAgentBooking.agent,
              id: selectedAgent.id,
              adxAgentId: selectedAgent.id,
              agentName: formValues.agentName,
              firstName: formValues.firstName,
              middleName: formValues.middleName,
              lastName: formValues.lastName,
              interfaceId: formValues.interfaceId,
              email: formValues.email || selectedAgent.email,
              tramsCentralDocumentId: selectedAgent.tramsCentralDocumentId
            };
            
            this._adxAgentBooking.agent = updatedAgent;
            
            this.currentAgentId = selectedAgent.id;
            this.isEdit = false;
            
            this.createForm();
            this.cdr.detectChanges();
          },
          error: () => {
            this._snackBar.open('Failed to update agent booking information.', 'Dismiss', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['message-position', 'app-notification-error']
            });
          }
      });
  }
}
